import Vue from 'vue'
import Constants from 'src/utilities/constants'
import {searchFieldsHaveValues} from 'src/utilities/utils'
import {createAndDownloadWorksheet} from 'src/utilities/excel'
import {
  advancedSearchBuilder,
  basicSearchBuilder,
  dateBetween,
  esClient,
  exactMetadataQueryBuilder,
  filterBuilder
} from 'src/utilities/ptracSearch'
import {make} from 'vuex-pathify'
import _ from 'lodash'
import {crudifyMutations} from "@/utilities/vuex2"

const initialState = {
  query: {
    search: null,
    active: true,
    projectType: null,
    createdBetween: [],
    streetNumber: null,
    streetName: null,
    city: null,
    zip: null,
    blockNumber: null,
    buildingNumber: null,
    floorNumber: null,
    county: null,
    jurisdiction: null,
    lotNumber: null,
    suiteNumber: null,
    state: Constants.ATS.DEFAULT.STATE,
    phase: null,
    section: null,
    legalSubdivisionId: null,
    legalDescription: null,
    metadata: null
  },
  results: []
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)

getters.results = (state) => {
  return state.results === undefined ? [] : state.results
}

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'search',
    'streetName',
    'streetNumber',
    'city',
    'zip',
    'blockNumber',
    'projectType',
    'buildingNumber',
    'floorNumber',
    'county',
    'jurisdiction',
    'lotNumber',
    'suiteNumber',
    'phase',
    'section',
    'legalSubdivisionId',
    'legalDescription',
    'createdBetween',
    'metadata'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

const mutations = make.mutations(state)
crudifyMutations(mutations)

mutations.resetResults = (state) => {
  state.results = []
}

mutations.results = (state, results) => {
  state.results = results
}

mutations.resetQuery = (state) => {
  for (let f in state.query) {
    Vue.set(state.query, f, initialState.query[f])
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchElasticsearchResults({state, commit, rootGetters}) {
      const searchFields = [
        'name',
        'address.blockNumber',
        'address.buildingNumber',
        'address.floorNumber',
        'address.city',
        'address.county',
        'address.jurisdiction',
        'address.lotNumber',
        'address.legalDescription',
        'address.legalSubdivisionId',
        'address.phase',
        'address.section',
        'address.state',
        'address.streetName',
        'address.streetNumber',
        'address.suiteNumber',
        'address.zip']

      // update this array when fields in project entities use hyphens
      const hyphenFields = [
        'address.section'
      ]

      const metadataFields = [
        'value',
        'name'
      ]

      const filterFields = [
        'projectType'
      ]

      const searchBody = {
        size: Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS,
        query: {
          bool: {
            must: [
              {match: {_class: Constants.ENTITY_FIELDS.PROJECT.CLASS}}
            ],
            should: [],
            filter: []
          }
        }
      }

      // Basic Search
      if (!!state.query.search) {
        const splitSearchTerms = state.query.search.toLowerCase().trim().split(' ')
        searchBody.query.bool.should.push(...basicSearchBuilder(searchFields, splitSearchTerms, hyphenFields, metadataFields))
        searchBody.query.bool['minimum_should_match'] = 1
      }

      searchBody.query.bool.must.push(...advancedSearchBuilder(searchFields, state.query))

      // Filter fields
      searchBody.query.bool.filter.push(...filterBuilder(filterFields, state.query))
      // Date filters
      if (state.query.createdBetween && state.query.createdBetween.length > 0) {
        searchBody.query.bool.filter.push(dateBetween(state.query.createdBetween, Constants.ENTITY_FIELDS.PROJECT.CREATED_AT))
      }
      // Metadata queries
      if (state.query.metadata) {
        searchBody.query.bool.must.push(exactMetadataQueryBuilder(Constants.ENTITY_FIELDS.PROJECT.METADATA, state.query.metadata))
      }
      // Active
      searchBody.query.bool.filter.push({term: {active: state.query.active}})

      if (Constants.ENVIRONMENTS.PRODUCTION !== process.env.VUE_APP_ENV) {
        console.log(JSON.stringify(searchBody, null, 2))
      }

      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)
      const response = await esClient(esCreds).search({
        index: 'ptrac',
        body: searchBody
      })

      commit('results', response.hits.hits)

      return response
    },
    resultsToExcel({state, rootState}) {
      const fileName = 'project-search-results.xlsx'
      const tabName = 'Projects'
      const header = [
        {
          header: 'Project Name',
          key: 'name',
          width: 30
        }, {
          header: 'Project Number',
          key: 'number',
          width: 15
        }, {
          header: 'Type',
          key: 'projectType',
          width: 20
        }, {
          header: 'Street Number',
          key: 'streetNumber',
          width: 15
        }, {
          header: 'Street Name',
          key: 'streetName',
          width: 20
        }, {
          header: 'City',
          key: 'city',
          width: 20
        }, {
          header: 'State',
          key: 'state',
          width: 10
        }, {
          header: 'Zip',
          key: 'zip',
          width: 15
        }]

      const projects = _.map(state.results, '_source')

      const rows = []
      projects.forEach((project) => {
        rows.push(
          {
            name: project.name,
            number: project.number,
            projectType: project.projectType,
            streetNumber: _.get(project, 'address.streetNumber'),
            streetName: _.get(project, 'address.streetName'),
            city: _.get(project, 'address.city'),
            state: _.get(project, 'address.state'),
            zip: _.get(project, 'address.zip'),
          }
        )
      })

      const user = rootState.security.user

      createAndDownloadWorksheet(fileName, tabName, header, rows, user)
    }
  }
}
